import React, { useRef, useEffect, useState, useCallback } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import useAccessToken from '../Custom/useAccessToken'; 
import { Skeleton } from 'primereact/skeleton';
import Profile from './Profile';
import { searchProfile } from '../Services/GetService';
import { useNavigate } from 'react-router-dom';

interface SearchSuggestionProps {
  searchQuery: string;
  searchBoxRef?: React.RefObject<HTMLDivElement | null>;
  closeSignal?: number;
}

const SearchSuggestions: React.FC<SearchSuggestionProps> = ({ searchQuery, searchBoxRef, closeSignal  }) => {
    
  const op = useRef<any>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const { accessToken } = useAccessToken();
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true); 
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [navMode, setNavMode] = useState(false);
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false);
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement | null>(null);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  useEffect(() => {
    if (op.current && targetRef.current) {
      if (searchQuery && searchQuery.trim() !== '') {
        setHighlightIndex(-1);
        setNavMode(false); 
        setIsKeyboardNavigation(false);
        op.current.show(targetRef.current); 
      } else {
        op.current.hide();  
      }
    }
  }, [searchQuery]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsSearching(false);
        op.current?.hide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
 
  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      const panelEl = op.current?.getElement();
      const boxEl = searchBoxRef?.current;
      const target = e.target as Node;

      const insidePanel = panelEl?.contains(target);
      const insideBox = boxEl?.contains(target);

      if (!insidePanel && !insideBox) {
        setIsSearching(false);
        op.current?.hide();
      }
    },
    [searchBoxRef]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {    

    if(closeSignal! > 0){
      setIsSearching(false);
      op.current?.hide();    
    }
     
  }, [closeSignal]);     

  useEffect(() => {
    if (searchQuery && searchQuery.trim() !== "") {
      setIsSearching(true);
  
      if (!accessToken) return;
  
      fetchSuggestions(10);
    } else {
      setIsSearching(false);
      setResults([]);
      setLoading(false);
    }
  }, [searchQuery, accessToken]);

  const fetchSuggestions = async (top_results: number) => {
    try {
      setLoading(true);
      const data = await searchProfile(accessToken, searchQuery, top_results);
      setResults(data ?? []);
    } catch (err) {
      console.error("Error fetching suggestions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOverlayVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!results || results.length === 0) return;
  
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setNavMode(true);
        setIsKeyboardNavigation(true);                         
        setHighlightIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setNavMode(true);
        setIsKeyboardNavigation(true);
        setHighlightIndex((prev) =>
          prev > 0 ? prev - 1 : results.length - 1
        );
      } else if (e.key === "Enter" && navMode && highlightIndex >= 0) {        
        e.preventDefault();
        const selected: any = results[highlightIndex];
        // navigate(`/profile/${selected.profileShortCode}`);

        navigate(`/profile/${selected.profileShortCode}`, {
          state: {
            from: "search",
            query:searchQuery
          },
        });

        setNavMode(false);
        setIsSearching(false);
        op.current?.hide();
      }
    };
  
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [results, highlightIndex, navMode, navigate]);    
  
  useEffect(() => {
    if (listRef.current && highlightIndex >= 0 && results.length > 0 && isKeyboardNavigation) {
      const list = listRef.current;
      const item = list.children[highlightIndex] as HTMLElement;
  
      if (item) {
        const itemTop = item.offsetTop;
        const itemHeight = item.offsetHeight;
        const itemBottom = itemTop + itemHeight;
        const listScrollTop = list.scrollTop;
        const listHeight = list.clientHeight;
        
        // Calculate visible area boundaries
        const visibleTop = listScrollTop;
        const visibleBottom = listScrollTop + listHeight;
        
        // Add some padding for better UX
        const padding = 5;
        
        // Check if item is above the visible area (scrolled too far down)
        if (itemTop < visibleTop + padding) {
          list.scrollTop = Math.max(0, itemTop - padding);
        } 
        // Check if item is below the visible area (need to scroll down)
        else if (itemBottom > visibleBottom - padding) {
          list.scrollTop = itemBottom - listHeight + padding;
        }
      }
    }
  }, [highlightIndex, results.length, isKeyboardNavigation]);

  const handleClose = () => {
    setIsSearching(false);
    setIsKeyboardNavigation(false);
    setHighlightIndex(-1);
    op.current?.hide();
  }

  const ProfileSkeleton = () => (
      <div className="profile-card">
          <div className="profile-left">
              <Skeleton shape="circle" size="45px" />
              <div className="profile-info">
                  <Skeleton
                      width="180px"
                      height="1.2rem"
                      className="profile-name mb-2"
                  />
                  <Skeleton
                      width="150px"
                      height="1.2rem"
                      className="profile-title mb-2"
                  />
              </div>
          </div>
          <div className="profile-right">
              <Skeleton width="160px" height="1.2rem" className="profile-meta mb-2" />
              <Skeleton width="130px" height="1.2rem" className="profile-meta mb-2" />
          </div>
      </div>
  );

  return (
    <>     

      {isSearching && (
        <div className="overlay-backdrop-content" onClick={handleClose}></div>
      )}

      <div ref={targetRef} ></div>

          <OverlayPanel ref={op}  onShow={() => setIsOverlayVisible(true)}
  onHide={() => setIsOverlayVisible(false)} className='custom-overlay'>
              <div className='overlay-search-content'>
                  {loading ? (
                      <div className="profiles-list">
                          {Array.from({ length: 5 }).map((_, i) => (
                              <ProfileSkeleton key={i} />
                          ))}
                      </div>
                  ) : results.length === 0 && isSearching ? (
                      <div className="no-search-results">
                          <h4>We didn’t find any matches.</h4>
                          <span className='mb-2'>
                              Try broader or alternative search terms such as name, role,
                              office, or skill.
                          </span>
                      </div>
                  ) : (
                      <>
                          <div className="profiles-list" ref={listRef}>
                              {results?.map((person: any, index: any) => (
                                  <div key={index} className={`cursor-pointer suggestion-item ${highlightIndex === index ? "active" : ""}`}
                                   onMouseEnter={() => {
                                     setIsKeyboardNavigation(false);
                                     setHighlightIndex(index);
                                   }}>
                                      <Profile
                                          key={index}
                                          {...person}
                                          isSearching={isSearching}
                                          searchQuery={searchQuery}
                                          onSelect={handleClose} 
                                      />
                                  </div>
                              ))}
                          </div>
                      </>
                  )}
              </div>
          </OverlayPanel>
    </>
  );
};

export default SearchSuggestions;